import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  UploadImage,
  UploadVedio,
  CreateAdvertisementAPI,
  UpdateAdvertisementAPI,
  GetAllDepartment,
} from "../../../lib/api";
import { RouteNavigation } from "../../../Routes";
import Cookies from "js-cookie";
import { constant } from "../../../lib/constant";

const CreateAdvertisement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const advertisementData = location?.state?.GetAdvertisement;
  const [isLoading, setIsLoading] = useState(false);
  const [media, setMedia] = useState({
    src: advertisementData?.AdvFileUrl || null,
    type: advertisementData?.AdvFileUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i)
      ? "image"
      : advertisementData?.AdvFileUrl?.match(/\.(mp4|webm|ogg)$/i)
      ? "video"
      : null,
  });
  const [isUploading, setIsUploading] = useState(false);
  const [mediaError, setMediaError] = useState("");
  const [currentDepartment, setCurrentDepartment] = useState(null);

  const { data: departmentsData } = useQuery({
    queryKey: ["GetAllDepartment"],
    queryFn: () => GetAllDepartment(),
  });

  const {
    register,
    handleSubmit,
    setFocus,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      advName: advertisementData?.AdvName || "",
      advOrder: advertisementData?.AdvOrder || "",
      depId: advertisementData?.DepID || "",
      status: advertisementData?.Status ?? true,
    },
  });
  const selectedDepartmentId = watch("depId");
  const mutation = useMutation({
    mutationFn: (data) =>
      advertisementData
        ? UpdateAdvertisementAPI(data, advertisementData.AdvID)
        : CreateAdvertisementAPI(data),
    onMutate: () => setIsLoading(true),
    onSuccess: () => {
      toast.success(
        `Advertisement ${
          advertisementData ? "updated" : "created"
        } successfully`,
        { position: "top-right", closeOnClick: true }
      );
      navigate(RouteNavigation.advertisements);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
          `Failed to ${advertisementData ? "update" : "create"} advertisement`,
        { position: "top-right", closeOnClick: true }
      );
    },
    onSettled: () => setIsLoading(false),
  });

  useEffect(() => {
    if (advertisementData && departmentsData?.data?.department) {
      const dept = departmentsData.data.department.find(
        (d) => d.DepId === advertisementData.DepId
      );
      setCurrentDepartment(dept);
    }
    setFocus("advName");
  }, [advertisementData, departmentsData, setFocus]);

  const handleFileUpload = async (file) => {
    setIsUploading(true);
    setMediaError("");

    try {
      const formData = new FormData();
      const isImage = file.type.startsWith("image/");
      const isVideo = file.type.startsWith("video/");

      if (!isImage && !isVideo) {
        throw new Error("Please upload a valid image or video file");
      }

      const maxSize = isImage ? 5 : 100;
      if (file.size > maxSize * 1024 * 1024) {
        throw new Error(`File size should not exceed ${maxSize}MB`);
      }

      formData.append(isImage ? "Image" : "Video", file);
      const uploadAPI = isImage ? UploadImage : UploadVedio;
      const response = await uploadAPI(formData);

      if (response.status !== 200) {
        throw new Error(`Failed to upload ${isImage ? "image" : "video"}`);
      }

      setMedia({
        src: isImage ? response.data?.image1 : response.data?.video1,
        type: isImage ? "image" : "video",
      });
    } catch (error) {
      setMediaError(error.message);
    } finally {
      setIsUploading(false);
    }
  };
  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  const onSubmit = async (formData) => {
    if (!media.src && !advertisementData?.AdvFileUrl) {
      setMediaError("Please upload an image or video");
      return;
    }

    const payload = {
      companyId: Number(Cookies.get("CompanyId")),
      depId: Number(selectedDepartmentId),
      advName: formData.advName,
      advOrder: formData.advOrder,
      advFileUrl: media.src || advertisementData?.AdvFileUrl,
      advModifiedDate: new Date().toISOString(),
      advModifiedUserId: Cookies.get("UserID"),
      status: formData.status,
    };

    mutation.mutate(payload);
  };

  return (
    <div className="py-5">
      <h4 className="text-3xl font-bold mb-5">
        {advertisementData ? "Update" : "Create"} Advertisement
      </h4>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col mt-5 gap-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="font-semibold block mb-2">
              Advertisement Name
            </label>
            <input
              {...register("advName", { required: "Name is required" })}
              className="w-full h-12 px-4 bg-gray-200 rounded focus:outline-none focus:ring-2"
            />
            {errors.advName && (
              <p className="text-red-600 mt-1">{errors.advName.message}</p>
            )}
          </div>
          <div>
            <label className="font-semibold block mb-2">Department</label>
            <select
              {...register("depId", { required: "Department is required" })}
              className="w-full h-12 px-4 bg-gray-200 rounded focus:outline-none focus:ring-2"
            >
              <option value="" disabled>
                Select Department
              </option>
              {departmentsData?.data?.department
                ?.filter(
                  (dept) =>
                    dept.Status || dept.DepID === advertisementData?.DepID
                )
                .map((dept) => (
                  <option
                    key={dept.DepId}
                    value={dept.DepID}
                    className={!dept.Status ? "text-gray-500" : ""}
                  >
                    {dept.DepName} {!dept.Status && "(Inactive)"}
                  </option>
                ))}
            </select>
            {errors.depId && (
              <p className="text-red-600 mt-1">{errors.depId.message}</p>
            )}
            {currentDepartment && !currentDepartment.Status && (
              <p className="text-yellow-600 mt-1">
                Note: This department is currently inactive
              </p>
            )}
          </div>

          <div>
            <label className="font-semibold block mb-2">Order</label>
            <input
              {...register("advOrder", {
                required: "Order is required",
                pattern: {
                  value: /^\d+$/,
                  message: "Please enter a valid number",
                },
              })}
              className="w-full h-12 px-4 bg-gray-200 rounded focus:outline-none focus:ring-2"
            />
            {errors.advOrder && (
              <p className="text-red-600 mt-1">{errors.advOrder.message}</p>
            )}
          </div>
        </div>

        {/* Media Upload */}
        <div className="space-y-4">
          <div>
            <label className="font-semibold block mb-2">
              Upload Image or Video
            </label>
            <input
              type="file"
              accept="image/*, video/*"
              onChange={handleFileChange}
              disabled={isUploading}
              className="w-full cursor-pointer"
            />
            <p className="text-sm text-gray-500 mt-1">
              Max size: 5MB (images) / 100MB (videos)
            </p>
            {mediaError && <p className="text-red-600 mt-1">{mediaError}</p>}
          </div>

          {/* Media Preview */}
          {media.src ? (
            <div className="mt-4 border rounded p-2">
              {media.type === "image" ? (
                <img
                  src={`${constant.image_render_url}${media.src}`}
                  alt="Preview"
                  className="max-h-80 mx-auto"
                />
              ) : (
                <video controls className="max-h-80 w-full">
                  <source
                    src={`${constant.image_render_url}${media.src}`}
                    type={`video/${media.src.split(".").pop()}`}
                  />
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          ) : (
            advertisementData?.AdvFileUrl && (
              <div className="mt-4 border rounded p-2">
                {/\.(jpg|jpeg|png|gif|webp)$/i.test(
                  advertisementData.AdvFileUrl
                ) ? (
                  <img
                    src={`${constant.image_render_url}${advertisementData.AdvFileUrl}`}
                    alt="Current"
                    className="max-h-80 mx-auto"
                  />
                ) : (
                  <video controls className="max-h-80 w-full">
                    <source
                      src={`${constant.image_render_url}${advertisementData.AdvFileUrl}`}
                      type={`video/${advertisementData.AdvFileUrl.split(
                        "."
                      ).pop()}`}
                    />
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
            )
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            {...register("status")}
            type="checkbox"
            id="statusCheckbox"
            className="w-4 h-4"
          />
          <label htmlFor="statusCheckbox" className="font-semibold">
            Active
          </label>
        </div>

        <div className="flex justify-end gap-4">
          <Link
            to={RouteNavigation.advertisements}
            className="px-6 py-2 bg-gray-600 rounded font-semibold text-white hover:bg-gray-700 transition"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isLoading || isUploading}
            className="px-6 py-2 bg-blue-600 rounded font-semibold text-white hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition"
          >
            {isLoading
              ? "Processing..."
              : advertisementData
              ? "Update Advertisement"
              : "Create Advertisement"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateAdvertisement;
